import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { usePendingEvaluations } from 'queries/admin/coach/usePendingEvaluations'

const PAGE_SIZE = 5

type PendingApprovalsTableState = {
  items: NonNullable<
    ReturnType<typeof usePendingEvaluations>['data']
  >['pages'][number]['assessments']
  isPending: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  isError: boolean
  errorMessage: string | undefined
}

type PendingApprovalsContextState = {
  state: {
    positionId: number
    upcoming: PendingApprovalsTableState
    past: PendingApprovalsTableState
  }
  actions: {
    loadMoreUpcoming: () => void
    loadMorePast: () => void
  }
}

const [PendingApprovalsContext, usePendingApprovalsContextValue] =
  createContext<PendingApprovalsContextState>({
    name: 'PendingApprovalsContext',
  })

const toTableState = (
  query: ReturnType<typeof usePendingEvaluations>
): PendingApprovalsTableState => {
  const {
    data,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    error,
  } = query

  return {
    items: data?.pages.flatMap((page) => page.assessments) ?? [],
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    isError,
    errorMessage:
      parseErrorMessage(error) ||
      (isError ? 'Failed to load pending approvals.' : undefined),
  }
}

type PendingApprovalsProviderProps = {
  positionId: number
  children: React.ReactNode
}

export const PendingApprovalsProvider = ({
  positionId,
  children,
}: PendingApprovalsProviderProps) => {
  const upcomingQuery = usePendingEvaluations({
    timeframe: 'upcoming',
    positionId,
    pageSize: PAGE_SIZE,
  })
  const pastQuery = usePendingEvaluations({
    timeframe: 'past',
    positionId,
    pageSize: PAGE_SIZE,
  })

  const value: PendingApprovalsContextState = {
    state: {
      positionId,
      upcoming: toTableState(upcomingQuery),
      past: toTableState(pastQuery),
    },
    actions: {
      loadMoreUpcoming: () => {
        void upcomingQuery.fetchNextPage()
      },
      loadMorePast: () => {
        void pastQuery.fetchNextPage()
      },
    },
  }

  return (
    <PendingApprovalsContext.Provider value={value}>
      {children}
    </PendingApprovalsContext.Provider>
  )
}

export { usePendingApprovalsContextValue }
